import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import LastSeen from '../LastSeen';
import VoiceOriginContext from '../../context/VoiceOriginContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faEye, faEyeSlash, faCircleInfo, faPhone,
    faCopy, faShare, faTrash, faSpinner, faHourglassHalf, faClockRotateLeft,
    faMagnifyingGlass, faXmark, faReply, faPen, faPhoneVolume, faListCheck
} from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import { resolveSameNameFiles } from '../../utils/sameNameFiles';
import { getSocket, onSocket, holdConversationRoom } from '../../utils/socket';
import EmployeeAvatar from '../EmployeeAvatar';
import GroupIcon from './GroupIcon';
import MessageBubble from './MessageBubble';
import MessageComposer from './MessageComposer';
import FileDropZone from '../FileDropZone';
import ForwardModal from './ForwardModal';
import PeoplePicker from './PeoplePicker';
import { SelectionBar, applyReactionLocal, copyText, transcript } from './MessageActions';
import { useCall } from '../../context/CallContext';
import { CHAT_PAGE_SIZE, mergeMessages } from '../../utils/chatCache';
import TypingIndicator from './TypingIndicator';
import MessageInfo from './MessageInfo';
import ChatSearchResults from './ChatSearchResults';
import { useRecentSearches } from '../RecentSearches';
import { preserveReceipts, tickState } from '../../utils/receipts';
import { newUploadId, requestUploadCancellation } from '../../utils/uploadCancellation';
import { expiryBanner } from '../../utils/groupExpiry';
import WithPresence from '../PresenceDot';

// Loaded on first use: it is only needed once a task is opened from an
// approval card, and it brings the router, which ChatWindow otherwise never
// touches (its tests render it without one).
const ChatTaskDrawer = React.lazy(() => import('./ChatTaskDrawer'));

/**
 * The right pane: one open conversation.
 *
 * Owns the message list, the socket subscription for this thread, and the
 * read-state round trip. The parent owns the conversation list, so anything
 * that changes the sidebar (a new message, a rename) is passed back up rather
 * than duplicated here.
 */

const daySeparator = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const EMPTY_OUTBOX = [];

/** What a message with no words reads as when copied as part of a selection. */
const attachmentLabel = (m) => {
    const first = (m.attachments || [])[0];
    if (!first) return '';
    return {
        image: '<Photo>',
        audio: '<Voice note>',
        video: '<Recording>'
    }[first.type] || `<${first.fileName || 'File'}>`;
};

const ChatWindow = ({
    conversation,
    myId,
    myRole,
    onBack,
    onToggleInfo,
    // The Tasks panel (direct chats only; Chats.js leaves these out for a group).
    onToggleTasks = null,
    tasksOpen = false,
    taskCount = null,
    // Tasks with discussion I have not read: a dot on the button.
    taskUnread = 0,
    onConversationChanged,
    jumpTarget = null,
    onJumpHandled,
    outboxMessages = EMPTY_OUTBOX,
    cache,
    manageRoom = true,
    // Inside something that draws its own title — the call window's chat
    // panel — so the conversation header is left out.
    embedded = false
}) => {
    const initialPage = cache?.peek(conversation?._id)?.messages;
    const [messages, setMessages] = useState(() => mergeMessages(outboxMessages, initialPage?.messages || []));
    const [loading, setLoading] = useState(!initialPage);
    const [hasMore, setHasMore] = useState(Boolean(initialPage?.hasMore));
    const [loadingMore, setLoadingMore] = useState(false);
    const [olderError, setOlderError] = useState('');
    const [replyTo, setReplyTo] = useState(null);
    const [editing, setEditing] = useState(null);
    const [typingPeople, setTypingPeople] = useState([]);
    const typingTimers = useRef(new Map());
    const olderPending = useRef(false);
    const scrollAnchor = useRef(null);
    const mounted = useRef(true);
    const lastCachedMessages = useRef(messages);
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const outboxRef = useRef(outboxMessages);
    outboxRef.current = outboxMessages;
    const refreshing = useRef(true);
    const previousScrollTop = useRef(0);

    // Select mode: which messages are ticked, and which are being forwarded.
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [forwardIds, setForwardIds] = useState(null);
    const [callPicking, setCallPicking] = useState(false);
    const [infoFor, setInfoFor] = useState(null);
    // A task opened from an approval card: { id, canDecide, onDecided }.
    const [viewTask, setViewTask] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const recentSearch = useRecentSearches('chat-messages', { value: searchQuery, onPick: setSearchQuery });
    const [localJumpTarget, setLocalJumpTarget] = useState(null);
    const requestedJump = useRef(null);

    const { startCall, joinCall, call: myCall, isBusy, maxParticipants = 20 } = useCall() || {};

    const threadRef = useRef(null);
    const composerRef = useRef(null);
    const bottomRef = useRef(null);
    // Whether the user is parked at the bottom. A new message must not yank the
    // view down while someone is reading back through history.
    const atBottomRef = useRef(true);
    // Only a new message should scroll the thread. A reaction landing on an
    // old one must not pull the reader down to the bottom.
    const lastCountRef = useRef(0);

    const conversationId = conversation?._id;

    /* --------------------------- loading messages --------------------------- */

    useEffect(() => {
        let cancelled = false;
        mounted.current = true;
        refreshing.current = true;
        const initialIds = new Set(messagesRef.current.map(message => String(message._id)));
        const confirmedOutboxIds = new Set(outboxRef.current.filter(message => !message.local)
            .map(message => String(message._id)));
        const load = async () => {
            try {
                const page = cache
                    ? await cache.loadMessages(conversationId)
                    : (await api.get(`/conversations/${conversationId}/messages`, { params: { limit: CHAT_PAGE_SIZE } })).data;
                if (cancelled) return;
                // Keep socket arrivals and confirmed uploads that beat an old
                // response. Do not keep every cached message: the server may
                // deliberately omit history this user has cleared.
                setMessages(prev => {
                    const snapshotIds = new Set((page.messages || []).map(message => String(message._id)));
                    const arrivals = prev.filter(message => message.local
                        || !initialIds.has(String(message._id))
                        || confirmedOutboxIds.has(String(message._id)))
                        .filter(message => !snapshotIds.has(String(message._id)));
                    const current = new Map(prev.map(message => [String(message._id), message]));
                    const snapshot = (page.messages || []).map(message =>
                        preserveReceipts(message, current.get(String(message._id))));
                    const next = mergeMessages(snapshot, arrivals);
                    // Reading a snapshot must not extend its freshness forever.
                    if (!arrivals.length) lastCachedMessages.current = next;
                    return next;
                });
                setHasMore(Boolean(page.hasMore));
            } catch (err) {
                if (!cancelled) Swal.fire('Error', err.response?.data?.message || 'Could not load messages.', 'error');
            } finally {
                if (!cancelled) { refreshing.current = false; setLoading(false); }
            }
        };
        if (conversationId) load();
        return () => { cancelled = true; mounted.current = false; };
    }, [conversationId, cache]);

    // An upload may finish while this chat is closed and then reopened. Keep the
    // thread in step with the page-owned outbox without caching unsent Files.
    useEffect(() => {
        const byClientId = new Map(outboxMessages.map(message => [message.clientId, message]));
        setMessages(prev => {
            const retained = prev.filter(message => !message.local || byClientId.get(message.clientId)?.local);
            const byId = new Map(retained.map(message => [String(message._id), message]));
            const byPreviousClientId = new Map(prev.filter(message => message.local)
                .map(message => [message.clientId, message]));
            const updates = outboxMessages
                // Confirmed copies held by the parent can be older than socket receipts.
                .filter(message => message.local || !byId.has(String(message._id)))
                .map(message => preserveReceipts(message,
                    byId.get(String(message._id)) || byPreviousClientId.get(message.clientId)));
            return mergeMessages(retained, updates);
        });
    }, [outboxMessages]);

    useEffect(() => {
        if (loading || messages === lastCachedMessages.current) return;
        lastCachedMessages.current = messages;
        // A message still sending, or refused, exists only in this tab. Caching
        // it would bring it back as a ghost the next time the chat is opened —
        // one that looks sent and never was.
        cache?.writeMessages(conversationId, messages.filter((m) => !m.local), hasMore);
    }, [messages, hasMore, loading, conversationId, cache]);

    const loadOlder = async () => {
        if (!messages.length || !hasMore || loading || refreshing.current || olderPending.current) return;
        olderPending.current = true;
        setLoadingMore(true);
        setOlderError('');

        try {
            const res = await api.get(`/conversations/${conversationId}/messages`, {
                params: { before: messages[0].createdAt, beforeId: messages[0]._id, limit: CHAT_PAGE_SIZE }
            });
            if (!mounted.current) return;
            const thread = threadRef.current;
            scrollAnchor.current = { height: thread?.scrollHeight || 0, top: thread?.scrollTop || 0 };
            setMessages((prev) => mergeMessages(res.data.messages || [], prev));
            setHasMore(Boolean(res.data.hasMore));
        } catch {
            if (mounted.current) setOlderError('Could not load older conversation.');
        } finally {
            olderPending.current = false;
            if (mounted.current) setLoadingMore(false);
        }
    };

    useLayoutEffect(() => {
        if (scrollAnchor.current && threadRef.current) {
            const { height, top } = scrollAnchor.current;
            threadRef.current.scrollTop = top + threadRef.current.scrollHeight - height;
            scrollAnchor.current = null;
            lastCountRef.current = messages.length;
        }
    }, [messages]);

    /* ------------------------------- realtime ------------------------------- */

    useEffect(() => {
        if (!conversationId) return;
        let active = true;
        const releaseRoom = manageRoom ? holdConversationRoom(conversationId) : null;

        const offNew = onSocket('message:new', (msg) => {
            if (String(msg.conversationId) !== String(conversationId)) return;
            setMessages((prev) => {
                // The sender may already have this from the POST response; the
                // echo would otherwise render it twice.
                if (prev.some((m) => m._id === msg._id)) return prev;
                /*
                 * Or it may be one of ours, still showing its clock. The push
                 * can beat the POST's own reply, so it has to be able to take
                 * the local copy's place itself — matched on the id this tab
                 * invented, which the server echoes and never stores.
                 */
                if (msg.clientId) {
                    const at = prev.findIndex((m) => m.local && m.clientId === msg.clientId);
                    if (at !== -1) {
                        const next = [...prev];
                        next[at] = preserveReceipts(msg, prev[at]);
                        return next;
                    }
                }
                return [...prev, msg];
            });
            const senderId = String(msg.sender?._id || msg.sender);
            if (senderId === String(myId) && msg.clientId) {
                onConversationChanged?.({ _id: conversationId, delivery: 'sent', clientId: msg.clientId, message: msg });
            }
            setTypingPeople(prev => prev.filter(person => person.id !== senderId));
        });

        // Every recipient gets this user-room event, even when its conversation
        // room has not joined yet or its message:new event was lost. The sidebar
        // handles the preview; the open thread must catch up independently.
        const offActivity = onSocket('conversation:activity', async ({ conversationId: cid }) => {
            if (String(cid) !== String(conversationId)) return;
            cache?.invalidate(conversationId);
            try {
                const { data } = await api.get(`/conversations/${conversationId}/messages`,
                    { params: { limit: CHAT_PAGE_SIZE } });
                if (!active) return;
                setMessages(prev => {
                    const current = new Map(prev.map(message => [String(message._id), message]));
                    const snapshot = (data.messages || []).map(message =>
                        preserveReceipts(message, current.get(String(message._id))));
                    return mergeMessages(prev, snapshot);
                });
            } catch { /* The reconnect fetch will retry if this request fails. */ }
        });

        const offEdit = onSocket('message:edited', (msg) => {
            if (String(msg.conversationId) !== String(conversationId)) return;
            setMessages((prev) => prev.map((m) => (m._id === msg._id ? msg : m)));
        });

        /*
         * A video finished converting on the server.
         *
         * The bytes behind an attachment changed without anybody editing the
         * message: a screen recording is served raw so it appears instantly,
         * and an H.264 copy replaces it a minute later. Without this the tab
         * that sent it keeps the raw url for as long as it stays open — which
         * is exactly the tab whose owner is waiting to watch it back.
         *
         * Patched field by field rather than replacing the message, so read
         * ticks and reactions this tab has already advanced are left alone.
         */
        const offMedia = onSocket('message:media', ({ conversationId: cid, messageId, mediaId, ...patch }) => {
            if (String(cid) !== String(conversationId)) return;
            setMessages((prev) => prev.map((m) => (String(m._id) !== String(messageId) ? m : {
                ...m,
                attachments: (m.attachments || []).map((a) => (
                    String(a._id) === String(mediaId) ? { ...a, ...patch } : a
                ))
            })));
        });

        const offDelete = onSocket('message:deleted', ({ _id, conversationId: cid }) => {
            if (String(cid) !== String(conversationId)) return;
            setMessages((prev) => prev.map((m) => (
                m._id === _id
                    ? { ...m, deletedAt: new Date().toISOString(), text: '', attachments: [], reactions: [] }
                    : m
            )));
            setSelectedIds((prev) => prev.filter((x) => x !== _id));
        });

        const offReaction = onSocket('message:reaction', ({ _id, conversationId: cid, reactions }) => {
            if (String(cid) !== String(conversationId)) return;
            setMessages((prev) => prev.map((m) => (m._id === _id ? { ...m, reactions } : m)));
        });

        /*
         * Somebody's browser has just come online and caught up: these messages
         * of mine have reached them. Sent to my own room rather than the
         * conversation's, so it arrives whichever chat I have open.
         */
        const offDelivered = onSocket('message:delivered', ({ conversationId: cid, messageIds, userId, at }) => {
            if (String(cid) !== String(conversationId)) return;
            const ids = new Set((messageIds || []).map(String));
            setMessages((prev) => prev.map((m) => (
                ids.has(String(m._id))
                    && !(m.deliveredTo || []).some((d) => String(d.user?._id || d.user) === String(userId))
                    ? { ...m, deliveredTo: [...(m.deliveredTo || []), { user: userId, at }] }
                    : m
            )));
        });

        const offRead = onSocket('message:read', ({ conversationId: cid, userId, at }) => {
            if (String(cid) !== String(conversationId)) return;
            setMessages((prev) => prev.map((m) => (
                (m.readBy || []).some((r) => String(r.user) === String(userId))
                    ? m
                    : { ...m, readBy: [...(m.readBy || []), { user: userId, at }] }
            )));
        });

        const timers = typingTimers.current;
        const offTyping = onSocket('conversation:typing', ({ conversationId: cid, userId, externalId, name, typing }) => {
            if (String(cid) !== String(conversationId) || !name || String(userId) === String(myId)) return;
            const id = String(userId || externalId || name);
            clearTimeout(timers.get(id));
            timers.delete(id);
            setTypingPeople(prev => typing
                ? [...prev.filter(person => person.id !== id), { id, name }]
                : prev.filter(person => person.id !== id));
            if (typing) timers.set(id, setTimeout(() => {
                timers.delete(id);
                setTypingPeople(prev => prev.filter(person => person.id !== id));
            }, 5000));
        });

        return () => {
            active = false;
            releaseRoom?.();
            offNew(); offActivity(); offEdit(); offMedia(); offDelete(); offReaction(); offDelivered(); offRead(); offTyping();
            timers.forEach(clearTimeout);
            timers.clear();
        };
    }, [conversationId, myId, manageRoom, onConversationChanged, cache]);

    // Escape leaves select mode, the way it closes everything else here.
    useEffect(() => {
        if (!selectMode) return;
        const onKey = (e) => {
            if (e.key === 'Escape' && !forwardIds) {
                setSelectMode(false);
                setSelectedIds([]);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [selectMode, forwardIds]);

    /* ---------------------------- scroll & read ---------------------------- */

    useEffect(() => {
        const grew = messages.length > lastCountRef.current;
        lastCountRef.current = messages.length;
        if (grew && atBottomRef.current) {
            bottomRef.current?.scrollIntoView({ block: 'end' });
        }
    }, [messages]);

    useEffect(() => {
        if (typingPeople.length && atBottomRef.current) bottomRef.current?.scrollIntoView({ block: 'end' });
    }, [typingPeople]);

    const onScroll = () => {
        const el = threadRef.current;
        if (!el) return;
        atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        const movingUp = el.scrollTop < previousScrollTop.current;
        previousScrollTop.current = el.scrollTop;
        if (movingUp && el.scrollTop < 80 && !olderError) loadOlder();
    };

    // Mark read once the thread has actually been rendered to the person.
    useEffect(() => {
        if (!conversationId || loading || !messages.length) return;
        const t = setTimeout(async () => {
            try {
                await api.post(`/conversations/${conversationId}/read`);
                onConversationChanged?.({ _id: conversationId, unread: 0 });
            } catch { /* a failed read receipt is not worth surfacing */ }
        }, 600);
        return () => clearTimeout(t);
    }, [conversationId, loading, messages.length, onConversationChanged]);

    /* ------------------------------- actions ------------------------------- */

    /*
     * Sending, WhatsApp-style: the message is in the thread the instant Send is
     * pressed, with a clock where the ticks go, and the box is empty and ready
     * for the next one.
     *
     * It used to wait for the server — attachments uploaded and all — before
     * showing anything, so the text sat in the box and it looked as though Send
     * had not been pressed. People pressed it again.
     *
     * Messages go out one at a time, in the order they were written. Sent in
     * parallel, a short reply typed after a long upload would reach the server
     * first and land above it in everybody else's thread.
     */
    const sendQueue = useRef(Promise.resolve());
    const uploadAttempts = useRef(new Map());
    const uploadControllers = useRef(new Map());
    const cancellingUploads = useRef(new Set());

    const deliver = useCallback(async (local, attempt) => {
        if (uploadAttempts.current.get(local.clientId) !== attempt) return;
        const { text, files, audioMeta, mentions, replyToId, reuse = [] } = local.outgoing;
        const data = new FormData();
        data.append('text', text);
        data.append('clientId', local.clientId);
        files.forEach((f) => data.append('attachments', f));
        // earlier files sent again: nothing to upload, the server attaches them
        if (reuse.length) data.append('reuse', JSON.stringify(reuse.map(({ messageId, attachmentId }) => ({ messageId, attachmentId }))));
        if (audioMeta) {
            data.append('waveform', JSON.stringify(audioMeta.waveform || []));
            data.append('durationMs', String(audioMeta.durationMs || 0));
        }
        if (mentions?.length) data.append('mentions', JSON.stringify(mentions));
        if (replyToId) data.append('replyToId', replyToId);

        const controller = new AbortController();
        const uploadId = files.length ? newUploadId() : null;
        uploadControllers.current.set(local.clientId, { controller, uploadId });
        if (files.length) {
            setMessages((prev) => prev.map((m) => m.local && m.clientId === local.clientId
                ? { ...m, uploadState: 'uploading', uploadProgress: null } : m));
            onConversationChanged?.({ _id: conversationId, delivery: 'progress', clientId: local.clientId, uploadState: 'uploading', uploadProgress: null });
        }

        try {
            const res = await api.post(`/conversations/${conversationId}/messages`, data, {
                headers: { 'Content-Type': 'multipart/form-data', ...(uploadId ? { 'x-upload-id': uploadId } : {}) },
                signal: controller.signal,
                onUploadProgress: files.length ? (event) => {
                    if (uploadAttempts.current.get(local.clientId) !== attempt || cancellingUploads.current.has(local.clientId)) return;
                    const progress = event.total ? Math.min(100, Math.round(event.loaded * 100 / event.total)) : null;
                    const uploadState = progress === 100 ? 'processing' : 'uploading';
                    setMessages((prev) => prev.map((m) => m.local && m.clientId === local.clientId
                        ? { ...m, uploadProgress: progress, uploadState } : m));
                    onConversationChanged?.({ _id: conversationId, delivery: 'progress', clientId: local.clientId, uploadProgress: progress, uploadState });
                } : undefined
            });
            setMessages((prev) => {
                // The socket push may have arrived first and taken its place.
                const arrived = prev.some((m) => m._id === res.data._id);
                return prev.flatMap((m) => {
                    if (!(m.local && m.clientId === local.clientId)) return [m];
                    return arrived ? [] : [preserveReceipts(res.data, m)];
                });
            });
            onConversationChanged?.({ _id: conversationId, delivery: 'sent', clientId: local.clientId, message: res.data });
        } catch (err) {
            if (uploadAttempts.current.get(local.clientId) !== attempt) return;
            /*
             * Kept in the thread, marked, with Retry and Delete — never quietly
             * dropped. The text was already cleared out of the box, so this is
             * now the only copy of what the person wrote.
             */
            setMessages((prev) => prev.map((m) => (
                m.local && m.clientId === local.clientId
                    ? {
                        ...m,
                        pending: false,
                        failed: true,
                        error: err.response?.data?.message || 'Could not send.'
                    }
                    : m
            )));
            onConversationChanged?.({
                _id: conversationId, delivery: 'failed', clientId: local.clientId,
                message: local, error: err.response?.data?.message || 'Could not send.'
            });
        } finally {
            if (uploadControllers.current.get(local.clientId)?.controller === controller) uploadControllers.current.delete(local.clientId);
            cancellingUploads.current.delete(local.clientId);
        }
    }, [conversationId, onConversationChanged]);

    const enqueue = useCallback((local) => {
        // deliver never throws, so one failure cannot stall the messages behind it.
        const attempt = (uploadAttempts.current.get(local.clientId) || 0) + 1;
        uploadAttempts.current.set(local.clientId, attempt);
        sendQueue.current = sendQueue.current.then(() => deliver(local, attempt));
    }, [deliver]);

    const send = async ({ text, files: picked, audioMeta, mentions, mentionPeople = [], replyToId }) => {
        // A document named like one this person sent here before: send that one
        // again, or upload this one as "name(1).ext". Cancel keeps it all in the box.
        let files = picked;
        let reuse = [];
        if (!audioMeta && picked.length) {
            const decided = await resolveSameNameFiles(conversationId, picked);
            if (!decided) return false;
            ({ files, reuse } = decided);
        }

        const clientId = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        const quoting = replyToId && replyTo && String(replyTo._id) === String(replyToId) ? replyTo : null;

        const local = {
            _id: `local:${clientId}`,
            clientId,
            local: true,
            pending: true,
            uploadState: files.length ? 'queued' : undefined,
            uploadProgress: null,
            conversationId,
            sender: { _id: myId },
            text,
            // Named rather than previewed: there is no URL for them until the
            // upload finishes.
            attachments: [
                ...reuse.map((r) => ({ fileName: r.fileName })),
                ...files.map((f) => ({ fileName: audioMeta ? 'Voice note' : f.name }))
            ],
            // The people themselves, not their ids, so the highlight shows now.
            mentions: mentionPeople,
            replyTo: quoting
                ? { messageId: quoting._id, text: quoting.text || '', senderName: quoting.sender?.name || '' }
                : undefined,
            readBy: [{ user: myId }],
            deliveredTo: [],
            reactions: [],
            createdAt: new Date().toISOString(),
            outgoing: { text, files, audioMeta, mentions, replyToId, reuse }
        };

        atBottomRef.current = true;
        setMessages((prev) => [...prev, local]);
        onConversationChanged?.({ _id: conversationId, delivery: 'sending', clientId, message: local });
        enqueue(local);
    };

    const retry = (message) => {
        setMessages((prev) => prev.map((m) => (
            m._id === message._id ? { ...m, pending: true, failed: false, error: '', uploadState: 'queued', uploadProgress: null } : m
        )));
        onConversationChanged?.({ _id: conversationId, delivery: 'sending', clientId: message.clientId, message: { ...message, uploadState: 'queued', uploadProgress: null } });
        enqueue(message);
    };

    const markUploadCancelled = (message) => {
        uploadAttempts.current.set(message.clientId, (uploadAttempts.current.get(message.clientId) || 0) + 1);
        setMessages((prev) => prev.map((m) => m.local && m.clientId === message.clientId
            ? { ...m, pending: false, failed: true, error: 'Upload cancelled.' } : m));
        onConversationChanged?.({ _id: conversationId, delivery: 'failed', clientId: message.clientId, message, error: 'Upload cancelled.' });
    };

    const cancelUpload = async (message) => {
        if (!message.local || !message.pending || !message.outgoing?.files?.length || cancellingUploads.current.has(message.clientId)) return;
        const active = uploadControllers.current.get(message.clientId);
        if (!active) {
            markUploadCancelled(message); // Still waiting in the send queue.
            return;
        }
        cancellingUploads.current.add(message.clientId);
        setMessages((prev) => prev.map((m) => m.local && m.clientId === message.clientId
            ? { ...m, uploadState: 'cancelling' } : m));
        onConversationChanged?.({ _id: conversationId, delivery: 'progress', clientId: message.clientId, uploadProgress: message.uploadProgress, uploadState: 'cancelling' });
        try {
            await requestUploadCancellation(active.uploadId);
            if (uploadControllers.current.get(message.clientId) !== active) return;
            markUploadCancelled(message);
            active.controller.abort();
        } catch (err) {
            if (uploadControllers.current.get(message.clientId) !== active) return;
            if (!err.response && message.uploadProgress !== 100) {
                // The network went away before the server received the body.
                markUploadCancelled(message);
                active.controller.abort();
            } else {
                setMessages((prev) => prev.map((m) => m.local && m.clientId === message.clientId
                    ? { ...m, uploadState: message.uploadProgress === 100 ? 'processing' : 'uploading' } : m));
                Swal.fire('Could not cancel', 'The server may already be saving this file. Waiting for its result.', 'info');
            }
        } finally {
            cancellingUploads.current.delete(message.clientId);
        }
    };

    const discard = (message) => {
        setMessages((prev) => prev.filter((m) => m._id !== message._id));
        onConversationChanged?.({ _id: conversationId, delivery: 'discarded', clientId: message.clientId });
    };

    const saveEdit = async (message, text) => {
        try {
            const res = await api.put(
                `/conversations/${conversationId}/messages/${message._id}`,
                { text }
            );
            setMessages((prev) => prev.map((m) => (m._id === message._id ? res.data : m)));
            setEditing(null);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not edit the message.', 'error');
        }
    };

    const markDeleted = (ids) => {
        const set = new Set(ids);
        setMessages((prev) => prev.map((m) => (
            set.has(m._id)
                ? { ...m, deletedAt: new Date().toISOString(), text: '', attachments: [], reactions: [] }
                : m
        )));
    };

    const remove = async (message) => {
        const ok = await Swal.fire({
            title: 'Delete this message?',
            text: 'It will be removed for everyone in this chat.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Delete'
        });
        if (!ok.isConfirmed) return;

        try {
            await api.delete(`/conversations/${conversationId}/messages/${message._id}`);
            markDeleted([message._id]);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not delete it.', 'error');
        }
    };

    /**
     * React, or take a reaction back.
     *
     * Applied locally first so the chip appears on the click, then replaced by
     * what the server stored. If the request fails, the earlier reactions are
     * put back rather than leaving a reaction nobody else can see.
     */
    const react = async (message, emoji) => {
        const before = message.reactions || [];
        const me = { _id: myId, name: conversation.myName };
        setMessages((prev) => prev.map((m) => (
            m._id === message._id ? { ...m, reactions: applyReactionLocal(m.reactions || [], me, emoji) } : m
        )));

        try {
            const res = await api.post(
                `/conversations/${conversationId}/messages/${message._id}/react`,
                { emoji }
            );
            setMessages((prev) => prev.map((m) => (
                m._id === message._id ? { ...m, reactions: res.data.reactions } : m
            )));
        } catch (err) {
            setMessages((prev) => prev.map((m) => (m._id === message._id ? { ...m, reactions: before } : m)));
            Swal.fire('Error', err.response?.data?.message || 'Could not save your reaction.', 'error');
        }
    };

    const jumpTo = (messageId) => {
        const el = document.getElementById(`msg-${messageId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'background .4s';
        el.style.background = 'rgba(18,140,126,.12)';
        setTimeout(() => { el.style.background = ''; }, 1200);
    };

    const target = jumpTarget || localJumpTarget;
    useEffect(() => {
        if (!target || String(target.conversationId) !== String(conversationId)) return;
        const element = threadRef.current?.querySelector(`[id="msg-${target.messageId}"]`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.style.transition = 'background .4s';
            element.style.background = 'rgba(35,103,213,.16)';
            setTimeout(() => { element.style.background = ''; }, 1200);
            if (jumpTarget) onJumpHandled?.();
            else { setLocalJumpTarget(null); setSearchOpen(false); }
            requestedJump.current = null;
            return;
        }
        const request = `${target.requestId}:${target.messageId}`;
        if (requestedJump.current === request) return;
        requestedJump.current = request;
        api.get(`/conversations/${conversationId}/messages/${target.messageId}/context`)
            .then(({ data }) => {
                if (mounted.current && requestedJump.current === request) {
                    setMessages(prev => mergeMessages(prev, data.messages || []));
                }
            })
            .catch(() => {
                if (!mounted.current || requestedJump.current !== request) return;
                requestedJump.current = null;
                Swal.fire('Message unavailable', 'This result could not be opened.', 'error');
                if (jumpTarget) onJumpHandled?.();
                else setLocalJumpTarget(null);
            });
    }, [target, jumpTarget, conversationId, messages, onJumpHandled]);

    const emitTyping = (typing) => {
        getSocket()?.emit('conversation:typing', {
            conversationId,
            name: conversation.myName,
            typing
        });
    };

    /** Join or leave a group silently (F3.10). Admin only. */
    const toggleOversight = async () => {
        const joining = !conversation.isMember;
        try {
            await api.post(`/conversations/${conversationId}/oversight`, { hidden: joining });
            onConversationChanged?.({ _id: conversationId, refetch: true });
            Swal.fire(
                joining ? 'Watching' : 'Stopped',
                joining
                    ? 'You can now post here. You are not shown in the member list, and this access is logged.'
                    : 'You are no longer watching this conversation.',
                'success'
            );
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not update oversight.', 'error');
        }
    };

    if (!conversation) return null;

    const isGroup = conversation.kind === 'group';
    // An outside app's notifications (server: routes/integrations.js): read
    // only, and nobody on the other end to call or reply to.
    const isApp = conversation.kind === 'app';
    const members = conversation.members || [];

    // null for everything that does not expire, which is everything except a
    // group a call made for itself. See utils/groupExpiry.
    const expiry = expiryBanner(conversation);

    // A call running in this group that we are not on: we left, dropped,
    // declined or missed it, or were never rung. The server only sends it to
    // members who may join (see announceOngoing in utils/callSignalling.js).
    const ongoing = conversation?.ongoingCall;
    const showOngoing = Boolean(ongoing?.callId)
        && conversation?.isMember && !conversation?.myHidden
        && String(myCall?.callId || '') !== String(ongoing.callId);
    const ongoingNames = (ongoing?.participants || []).map((p) => p.name).filter(Boolean);
    // Already on it from another device: this one can come in too, up to two.
    const ongoingDevices = ongoing?.you?.devices || [];
    const onTwoDevices = ongoingDevices.length >= 2;
    const deviceWord = (d) => (d === 'phone' ? 'Phone' : 'PC');

    /*
     * Placing the call, or choosing who to ring first.
     *
     * A group inside the cap rings everybody. A group over it cannot — the
     * server refuses rather than truncating — so the picker opens instead,
     * seeded from the membership this component already holds. No extra request:
     * `members` is populated with names by the conversation payload.
     */
    const overCapacity = isGroup && members.length > maxParticipants;

    const onCallPressed = () => {
        if (overCapacity) { setCallPicking(true); return; }
        startCall?.(conversation, 'audio');
    };
    // A person's header is their name and presence (LastSeen, below) only;
    // designation and department live in the details pane.
    // A one-to-one chat with a guest is a group underneath; it reads as a DM.
    const subtitle = conversation.isGuestDirect
        ? 'Guest · from outside GTS'
        : isApp
            ? 'Notifications from a connected app'
            : isGroup
                ? members.map((m) => m.user?.name).filter(Boolean).slice(0, 6).join(', ')
                : '';

    const senderOf = (m) => m.sender?._id || m.sender;
    const isMine = (m) => String(senderOf(m)) === String(myId);

    /*
     * Mirrors the server's rule exactly (canManage returns false for a direct
     * chat). Without the isGroup guard an admin saw a delete button on the
     * other person's messages in their OWN DMs and got a 403 for pressing it
     * — nobody moderates a private two-person conversation.
     */
    const canDelete = (m) => isMine(m) || (isGroup && (
        conversation.myRole === 'owner'
        || conversation.myRole === 'admin'
        || myRole === 'ADMIN'
        || myRole === 'HR'
    ));

    // Forwarding copies content out of this chat, so it needs membership like
    // posting does. Reacting also needs you to be visible: a hidden oversight
    // member's name on a reaction would give them away.
    const canForward = conversation.isMember;
    const canReact = conversation.isMember && !conversation.myHidden;

    /* ------------------------------ select mode ------------------------------ */

    const selectable = (m) => !m.systemEvent?.type && !m.deletedAt;

    const startSelect = (m) => {
        setSelectMode(true);
        setSelectedIds(selectable(m) ? [m._id] : []);
    };

    const toggleSelect = (m) => {
        if (!selectable(m)) return;
        const next = selectedIds.includes(m._id)
            ? selectedIds.filter((x) => x !== m._id)
            : [...selectedIds, m._id];
        // Unticking the last one ends selecting, as it does in the app.
        if (!next.length) setSelectMode(false);
        setSelectedIds(next);
    };

    const exitSelect = () => {
        setSelectMode(false);
        setSelectedIds([]);
    };

    const selectedMessages = messages.filter((m) => selectedIds.includes(m._id));
    // One message selected: it gets the reactions and the one-message actions.
    // Several: only what makes sense for a pile — copy, forward, delete.
    const onlySelected = selectedMessages.length === 1 ? selectedMessages[0] : null;

    const reactFromSelection = (m, emoji) => {
        react(m, emoji);
        exitSelect();
    };

    const singleAction = (fn) => () => {
        const m = onlySelected;
        exitSelect();
        if (m) fn(m);
    };

    const copySelected = () => {
        if (!selectedMessages.length) return;
        if (selectedMessages.length === 1) {
            const only = selectedMessages[0];
            copyText(only.text || attachmentLabel(only));
        } else {
            copyText(
                transcript(selectedMessages.map((m) => ({
                    at: m.createdAt,
                    name: isMine(m) ? (conversation.myName || 'You') : (m.sender?.name || m.externalSender?.name || 'Unknown'),
                    text: m.text || attachmentLabel(m)
                }))),
                `Copied ${selectedMessages.length} messages`
            );
        }
        exitSelect();
    };

    const deleteSelected = async () => {
        const targets = selectedMessages;
        if (!targets.length || !targets.every(canDelete)) return;

        const ok = await Swal.fire({
            title: `Delete ${targets.length} message${targets.length > 1 ? 's' : ''}?`,
            text: 'They will be removed for everyone in this chat.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Delete'
        });
        if (!ok.isConfirmed) return;

        const results = await Promise.allSettled(targets.map((m) => (
            api.delete(`/conversations/${conversationId}/messages/${m._id}`)
        )));
        markDeleted(targets.filter((_, i) => results[i].status === 'fulfilled').map((m) => m._id));

        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed) {
            Swal.fire('Some were not deleted', `${failed} message${failed > 1 ? 's' : ''} could not be deleted.`, 'error');
        }
        exitSelect();
    };

    const onForwarded = (targetIds) => {
        setForwardIds(null);
        exitSelect();
        // The server tells everyone else's sidebar; ours moves the rows up here,
        // as a normal send does.
        targetIds.forEach((id) => onConversationChanged?.({ _id: id, bump: true }));
    };

    let lastDay = null;
    let lastSender = null;

    // For the floating player: which chat a voice note came from, and the way
    // back to it - Chats.js reads `jumpToMessageId` and opens the thread there.
    // A plain object, not useMemo: this is past the `if (!conversation)` early return.
    const voiceOrigin = {
        title: conversation.title,
        href: `/chats/${conversationId}`,
        jumpState: (messageId) => ({ jumpToMessageId: String(messageId) })
    };

    return (
        <VoiceOriginContext.Provider value={voiceOrigin}>
        <FileDropZone
            className={embedded ? 'msgr-main is-embedded' : 'msgr-main'}
            autoSelectPaste
            disabled={!conversation.isMember || isApp || !!editing}
            onFiles={(files) => composerRef.current?.addFiles(files)}
            label="Drop files to send in this chat"
        >
            {selectMode ? (
                <div className="msgr-main-head is-selecting">
                    <SelectionBar
                        count={selectedIds.length}
                        onCancel={exitSelect}
                        actions={[
                            onlySelected && conversation.isMember && {
                                key: 'reply', label: 'Reply', icon: faReply, onClick: singleAction(setReplyTo)
                            },
                            onlySelected && isMine(onlySelected) && {
                                key: 'info', label: 'Info', icon: faCircleInfo, onClick: singleAction(setInfoFor)
                            },
                            onlySelected && isMine(onlySelected) && onlySelected.text && conversation.isMember && {
                                key: 'edit', label: 'Edit', icon: faPen, onClick: singleAction(setEditing)
                            },
                            { key: 'copy', label: 'Copy', icon: faCopy, onClick: copySelected },
                            canForward && {
                                key: 'forward',
                                label: 'Forward',
                                icon: faShare,
                                onClick: () => setForwardIds(selectedIds)
                            },
                            {
                                key: 'delete',
                                label: 'Delete',
                                icon: faTrash,
                                danger: true,
                                onClick: deleteSelected,
                                disabled: !selectedMessages.every(canDelete),
                                title: selectedMessages.every(canDelete)
                                    ? 'Delete for everyone'
                                    : 'You can only delete messages you are allowed to remove'
                            }
                        ]}
                    />
                </div>
            ) : !embedded && (
                <div className="msgr-main-head">
                    <button className="msgr-icon-btn msgr-back-btn" onClick={onBack} title="Back">
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>

                    {isGroup || isApp ? (
                        <GroupIcon conversation={conversation} className="msgr-avatar" />
                    ) : (
                        <WithPresence userId={conversation.otherUser?._id}>
                            <EmployeeAvatar
                                person={conversation.otherUser || {
                                    name: conversation.title,
                                    profilePic: conversation.avatarUrl
                                }}
                                className="msgr-avatar"
                            />
                        </WithPresence>
                    )}

                    <div className="msgr-main-title" onClick={onToggleInfo}>
                        <strong>{conversation.title}</strong>
                        <div className="msgr-main-sub">
                            {subtitle}
                            {!isGroup && !isApp && <div><LastSeen userId={conversation.otherUser?._id} /></div>}
                        </div>
                    </div>

                    {/*
                        Voice call (Module 8, F8.1 and F8.3).

                        Groups included, up to the participant cap the server
                        publishes (media goes through the LiveKit media server).
                        Beyond the cap a call is refused. That is why an
                        oversized group opens a picker instead of dialling: the
                        alternative is ringing the first few and silently leaving
                        the rest out of a call their group was told about.

                        Hidden for an oversight admin reading a conversation they
                        are not in: the server refuses that call anyway (an
                        unannounced watcher who rings the room stops being
                        unannounced), and a button whose only outcome is an error
                        is worse than no button.
                    */}
                    {conversation.isMember && !conversation.myHidden && !isApp && (
                        <button
                            className="msgr-icon-btn msgr-call-btn"
                            onClick={onCallPressed}
                            disabled={isBusy}
                            title={isBusy
                                ? 'You are already in a call'
                                : (isGroup ? `Call ${conversation.title}` : `Call ${conversation.title}`)}
                        >
                            <FontAwesomeIcon icon={faPhone} />
                        </button>
                    )}

                    {conversation.isMember && <button className={`msgr-icon-btn ${searchOpen ? 'is-on' : ''}`}
                        onClick={() => { setSearchOpen(value => !value); setSearchQuery(''); }}
                        title="Search this chat" aria-label="Search this chat">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </button>}

                    {onToggleTasks && (
                        <button
                            className={`msgr-icon-btn msgr-tasks-btn ${tasksOpen ? 'is-on' : ''}`}
                            onClick={onToggleTasks}
                            title="Tasks"
                            aria-label={[
                                'Tasks',
                                taskCount ? `${taskCount} open` : '',
                                taskUnread ? `unread discussion on ${taskUnread}` : ''
                            ].filter(Boolean).join(', ')}
                        >
                            <FontAwesomeIcon icon={faListCheck} />
                            {taskCount > 0 && <span className="msgr-tasks-badge">{taskCount > 99 ? '99+' : taskCount}</span>}
                            {taskUnread > 0 && <span className="msgr-tasks-dot" />}
                        </button>
                    )}

                    {/* On a phone the name above already opens the details. */}
                    <button className="msgr-icon-btn msgr-info-btn" onClick={onToggleInfo} title="Details">
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </button>
                </div>
            )}

            {searchOpen && !embedded && <div className="msgr-thread-search">
                <div className="msgr-thread-search-top">
                    <input {...recentSearch.inputProps} autoFocus maxLength={80} value={searchQuery} onChange={event => setSearchQuery(event.target.value)}
                        placeholder="Search messages and files in this chat" aria-label="Search this chat" />
                    {recentSearch.menu}
                    <button className="msgr-icon-btn" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                        title="Close search" aria-label="Close search"><FontAwesomeIcon icon={faXmark} /></button>
                </div>
                <ChatSearchResults query={searchQuery} conversationId={conversationId}
                    onMessage={hit => { recentSearch.save(); setLocalJumpTarget({ conversationId, messageId: String(hit._id), requestId: Date.now() }); }} />
            </div>}

            {/*
                The countdown on a group a call left behind.

                Shown to everybody in it, not only to an admin: the way to keep
                the group is to use it, and a deletion date nobody is told
                about is not a warning. The wording lives in utils/groupExpiry
                so this strip and the admin list cannot disagree about how long
                is left.
            */}
            {expiry && (
                <div className={`msgr-expiry-bar is-${expiry.tone}`}>
                    <FontAwesomeIcon icon={expiry.tone === 'danger' ? faClockRotateLeft : faHourglassHalf} />
                    <span>{expiry.text}</span>
                </div>
            )}

            {showOngoing && !embedded && (
                <div className="msgr-ongoing-call-bar" role="status">
                    <FontAwesomeIcon icon={faPhoneVolume} />
                    <span className="msgr-ongoing-call-text">
                        {ongoingDevices.length ? (
                            <>
                                <strong>You're on this {ongoing.kind === 'video' ? 'video call' : 'call'}</strong>
                                {' '}on {ongoingDevices.map(deviceWord).join(' and ')}
                            </>
                        ) : (
                            <>
                                <strong>{ongoing.kind === 'video' ? 'Video call' : 'Call'} in progress</strong>
                                {ongoing.count > 0 && (
                                    <> · {ongoingNames.slice(0, 3).join(', ')}
                                        {ongoing.count > 3 ? ` +${ongoing.count - 3}` : ''}</>
                                )}
                            </>
                        )}
                    </span>
                    {!onTwoDevices && (
                        <button
                            onClick={() => joinCall?.({
                                callId: ongoing.callId,
                                kind: ongoing.kind,
                                conversationId: conversationId,
                                conversationTitle: conversation.title || conversation.name || '',
                                isGroup: conversation.kind === 'group',
                                asSecondDevice: ongoingDevices.length > 0
                            })}
                            disabled={isBusy}
                            title={isBusy
                                ? 'You are already in a call'
                                : (ongoingDevices.length ? 'Join on this device too, muted' : 'Join this call')}
                        >
                            {ongoingDevices.length ? 'Join from here' : 'Join'}
                        </button>
                    )}
                </div>
            )}

            {/* Only an ADMIN ever sees this strip; ordinary members are always
                members, and the server refuses oversight to everyone else. */}
            {myRole === 'ADMIN' && (!conversation.isMember || conversation.myHidden) && (
                <div className="msgr-oversight-bar">
                    <FontAwesomeIcon icon={conversation.isMember ? faEyeSlash : faEye} />
                    {conversation.isMember
                        ? 'You are in this chat silently — members cannot see you here. This access is logged.'
                        : 'Oversight view. You are reading a conversation you are not a member of; this is logged.'}
                    <button onClick={toggleOversight}>
                        {conversation.isMember ? 'Stop watching' : 'Watch silently'}
                    </button>
                </div>
            )}

            <div className="msgr-thread" ref={threadRef} onScroll={onScroll}>
                {hasMore && (
                    <div className="msgr-history-status" role="status">
                        {loadingMore ? <><FontAwesomeIcon icon={faSpinner} className="msgr-history-spinner" /> loading older conversation</>
                            : olderError ? <>{olderError} <button type="button" onClick={loadOlder}>Retry</button></>
                                : <button type="button" onClick={loadOlder}>Load older conversation</button>}
                    </div>
                )}

                {loading && <div className="msgr-empty">Loading…</div>}

                {!loading && !messages.length && (
                    <div className="msgr-empty">No messages yet. Say hello.</div>
                )}

                {messages.map((m) => {
                    const day = daySeparator(m.createdAt);
                    const showDay = day !== lastDay;
                    lastDay = day;

                    const senderId = senderOf(m);
                    const mine = isMine(m);
                    const firstOfRun = String(senderId) !== String(lastSender);
                    lastSender = senderId;

                    return (
                        <React.Fragment key={m._id}>
                            {showDay && <div className="msgr-daysep">{day}</div>}
                            {/* No wrapper element here: the bubble must be a
                                direct child of the flex column for align-self
                                to put it on the correct side. */}
                            <MessageBubble
                                id={`msg-${m._id}`}
                                message={m}
                                mine={mine}
                                myId={myId}
                                showAuthor={isGroup && firstOfRun}
                                firstOfRun={firstOfRun}
                                tick={mine ? tickState(m, members, myId) : null}
                                onInfo={mine ? setInfoFor : null}
                                onRetry={retry}
                                onDiscard={discard}
                                onCancelUpload={cancelUpload}
                                onReply={conversation.isMember && !isApp ? setReplyTo : null}
                                onEdit={setEditing}
                                onDelete={canDelete(m) ? remove : null}
                                onJumpTo={jumpTo}
                                onReact={canReact ? react : null}
                                onForward={canForward ? (msg) => setForwardIds([msg._id]) : null}
                                onSelect={startSelect}
                                selectMode={selectMode}
                                selected={selectedIds.includes(m._id)}
                                onSelectionReact={
                                    canReact && onlySelected?._id === m._id ? reactFromSelection : null
                                }
                                onToggleSelect={toggleSelect}
                                onViewTask={(id, canDecide, onDecided, opts) => setViewTask({ id, canDecide, onDecided, ...opts })}
                                /*
                                 * Tapping a call entry calls back, the way it
                                 * does on a phone. Only where a call could
                                 * actually be placed: not in a group, and not
                                 * while one is already running — nor while
                                 * selecting, where a tap means "tick this".
                                 */
                                onCallBack={
                                    !isGroup && conversation.isMember
                                        && !conversation.myHidden && !isBusy && !selectMode
                                        ? () => startCall?.(conversation, 'audio')
                                        : null
                                }
                            />
                        </React.Fragment>
                    );
                })}

                <TypingIndicator people={typingPeople.map(person => ({
                    ...person,
                    ...(members.find(member => String(member.user?._id || member.user) === person.id)?.user
                        || (String(conversation.otherUser?._id) === person.id ? conversation.otherUser : {}))
                }))} />
                <div ref={bottomRef} />
            </div>

            <MessageComposer
                ref={composerRef}
                conversation={conversation}
                members={members}
                myId={myId}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                editing={editing}
                onCancelEdit={() => setEditing(null)}
                onSend={send}
                onSaveEdit={saveEdit}
                onTyping={emitTyping}
                disabled={!conversation.isMember || isApp}
                disabledReason={isApp
                    ? 'This chat only receives notifications from the app. Replies are not supported.'
                    : 'You are reading this as an admin. Choose “Watch silently” above to post here.'}
            />

            {infoFor && (
                <MessageInfo
                    conversationId={conversationId}
                    message={infoFor}
                    isGroup={isGroup}
                    onClose={() => setInfoFor(null)}
                />
            )}

            {viewTask && (
                <React.Suspense fallback={null}>
                    <ChatTaskDrawer
                        taskId={viewTask.id}
                        myRole={myRole}
                        canDecide={viewTask.canDecide}
                        onDecided={viewTask.onDecided}
                        focusDiscussion={Boolean(viewTask.discussion)}
                        onClose={() => setViewTask(null)}
                    />
                </React.Suspense>
            )}

            {forwardIds && (
                <ForwardModal
                    messageIds={forwardIds}
                    onClose={() => setForwardIds(null)}
                    onForwarded={onForwarded}
                />
            )}

            {/*
                Too many people in this group to ring them all, so the caller
                picks. Restricted to the group's own membership: reaching anyone
                else is what Add people inside the call is for, and letting this
                dial outside the group would mean a call that the group's own
                members were not invited to.
            */}
            {callPicking && (
                <PeoplePicker
                    title={`Who should join? (up to ${maxParticipants - 1})`}
                    excludeIds={[myId]}
                    people={members.map((m) => m.user).filter(Boolean)}
                    multi
                    maxSelect={maxParticipants - 1}
                    confirmLabel="Call them"
                    onConfirm={(ids) => {
                        setCallPicking(false);
                        startCall?.(conversation, 'audio', ids);
                    }}
                    onCancel={() => setCallPicking(false)}
                />
            )}
        </FileDropZone>
        </VoiceOriginContext.Provider>
    );
};

export default ChatWindow;
